import { IonContent, IonPage } from '@ionic/react';
import { useHistory } from 'react-router-dom';
import { Logo } from '@/components/ui/Logo';

const LAST_UPDATED = 'April 28, 2026';

export default function PrivacyPage() {
  const history = useHistory();

  return (
    <IonPage>
      <IonContent scrollY fullscreen>
        <header className="sticky top-0 z-10 border-b border-gray-200 bg-white">
          <div className="mx-auto flex max-w-3xl items-center gap-3 px-5 py-3">
            <button
              onClick={() => history.goBack()}
              className="flex h-9 w-9 items-center justify-center rounded-lg text-gray-500 hover:bg-gray-100"
              aria-label="Back"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M15 18l-6-6 6-6"/>
              </svg>
            </button>
            <Logo size="sm" />
          </div>
        </header>

        <main className="mx-auto max-w-3xl px-5 py-10 pb-24">
          <h1 className="font-serif text-3xl font-bold text-gray-900">Privacy Policy</h1>
          <p className="mt-2 text-sm text-gray-400">Last updated: {LAST_UPDATED}</p>

          <div className="prose prose-gray mt-8 max-w-none text-[0.95rem] leading-relaxed">

            <p className="text-gray-600">
              Open iPub ("we", "us", "our") is a reading and annotation platform at{' '}
              <a href="https://openipub.com" className="text-[#1a56a0] hover:underline">openipub.com</a>.
              This policy explains what information we collect, how we use it, and what your rights are.
              The short version: we collect only what we need to run the service, we don't sell your data, and we never will.
            </p>

            <Section title="1. Information We Collect">
              <p>We collect the following categories of information:</p>

              <h4 className="mt-4 font-semibold text-gray-800">Account information</h4>
              <ul className="mt-2 list-disc pl-5 text-gray-600">
                <li><strong>Email address</strong> — required for authentication. Used only to send you sign-in codes and occasional service notices. Never used for marketing without your consent.</li>
                <li><strong>Handle</strong> — the unique username you choose (e.g. @yourname). Publicly visible on your profile.</li>
                <li><strong>Display name</strong> — optional name you add to your profile. Publicly visible if set.</li>
                <li><strong>Bio</strong> — optional short description you add to your profile. Publicly visible if set.</li>
              </ul>

              <h4 className="mt-4 font-semibold text-gray-800">Content you create</h4>
              <ul className="mt-2 list-disc pl-5 text-gray-600">
                <li><strong>Publications and documents</strong> — content you write and publish through the app. Visible according to the visibility setting you choose (public or private).</li>
                <li><strong>Annotations</strong> — text passages you highlight and notes you attach. Visibility is per-annotation: public, private, or author-only. Only public annotations are visible to other readers.</li>
              </ul>

              <h4 className="mt-4 font-semibold text-gray-800">Usage data</h4>
              <ul className="mt-2 list-disc pl-5 text-gray-600">
                <li><strong>View counts</strong> — documents track how many times they've been viewed. This is aggregate data; we do not build personal reading history profiles.</li>
                <li><strong>Server logs</strong> — IP addresses, browser type, operating system, and request timestamps. Used for security, debugging, and abuse prevention. Retained for up to 90 days.</li>
              </ul>
            </Section>

            <Section title="2. How We Use Your Information">
              <p>We use the information we collect to:</p>
              <ul className="mt-2 list-disc pl-5 text-gray-600">
                <li>Authenticate you and keep your account secure</li>
                <li>Deliver sign-in codes and password-reset emails</li>
                <li>Display your profile and annotations to other users, according to the visibility settings you choose</li>
                <li>Count document views so authors can understand their readership</li>
                <li>Diagnose technical problems and prevent abuse</li>
                <li>Send you important service updates (e.g. changes to this policy, security notices)</li>
              </ul>
              <p className="mt-3 text-gray-600">
                We do not use your information to show you targeted advertisements. We do not build advertising profiles. We do not sell, rent, or trade your personal information to third parties.
              </p>
            </Section>

            <Section title="3. Third Parties We Share Data With">
              <p>We use a small number of trusted service providers to operate Open iPub:</p>

              <div className="mt-4 flex flex-col gap-5">
                <ThirdParty
                  name="Supabase"
                  url="https://supabase.com"
                  purpose="Database, authentication, and transactional email (sign-in codes). Your email address and account data are stored in Supabase's infrastructure. Supabase is GDPR compliant and may store data in the United States and European Union."
                />
                <ThirdParty
                  name="Google (if you use Google sign-in)"
                  url="https://policies.google.com/privacy"
                  purpose="If you choose 'Continue with Google', Google authenticates you and tells us who you are. We receive only your email address and name from Google. You can use email sign-in instead to avoid involving Google."
                />
                <ThirdParty
                  name="Vercel"
                  url="https://vercel.com/legal/privacy-policy"
                  purpose="Hosts the Open iPub web application. Vercel processes HTTP requests, which may include your IP address."
                />
              </div>

              <p className="mt-4 text-gray-600">
                We require all service providers to treat your data with the same care we do. We do not share your personal information with any other third parties, except when required by law.
              </p>
            </Section>

            <Section title="4. Cookies and Local Storage">
              <p>
                Open iPub uses browser local storage and session cookies to maintain your signed-in state. These are functional — the app cannot work without them. We do not use tracking cookies, advertising cookies, or third-party analytics cookies.
              </p>
              <p className="mt-3 text-gray-600">
                You can clear your local storage at any time through your browser settings, which will sign you out of the app.
              </p>
            </Section>

            <Section title="5. Data Retention and Deletion">
              <p>
                We retain your account information for as long as your account is active. If you would like to delete your account and all associated data — including annotations, publications, and profile information — email us at{' '}
                <a href="mailto:privacy@openipub.com" className="text-[#1a56a0] hover:underline">privacy@openipub.com</a>.
                We will complete deletion within 30 days.
              </p>
              <p className="mt-3 text-gray-600">
                Server log data is automatically deleted after 90 days.
              </p>
            </Section>

            <Section title="6. Security">
              <p>
                We take reasonable measures to protect your information against unauthorised access, alteration, disclosure, or destruction. Authentication is handled by Supabase, which uses industry-standard encryption and does not store passwords (we use magic-link and OAuth sign-in only). Database access is protected by row-level security policies so that users can only access data they are permitted to see.
              </p>
              <p className="mt-3 text-gray-600">
                No method of internet transmission is 100% secure. If you believe your account has been compromised, please contact us immediately.
              </p>
            </Section>

            <Section title="7. Children's Privacy">
              <p>
                Open iPub is not directed at children under the age of 13. We do not knowingly collect personal information from children under 13. If you believe we have inadvertently collected such information, please contact us and we will delete it promptly.
              </p>
            </Section>

            <Section title="8. Your Rights">
              <p>Depending on where you live, you may have the following rights:</p>
              <ul className="mt-2 list-disc pl-5 text-gray-600">
                <li><strong>Access</strong> — request a copy of the personal data we hold about you</li>
                <li><strong>Correction</strong> — ask us to correct inaccurate information</li>
                <li><strong>Deletion</strong> — request deletion of your account and personal data</li>
                <li><strong>Portability</strong> — receive your data in a machine-readable format</li>
                <li><strong>Objection</strong> — object to processing of your data in certain circumstances</li>
              </ul>
              <p className="mt-3 text-gray-600">
                To exercise any of these rights, email{' '}
                <a href="mailto:privacy@openipub.com" className="text-[#1a56a0] hover:underline">privacy@openipub.com</a>.
              </p>
            </Section>

            <Section title="9. Changes to This Policy">
              <p>
                We may update this policy from time to time. If we make material changes, we will notify signed-in users by email. The "Last updated" date at the top of this page will always reflect the most recent revision. Your continued use of Open iPub after any change constitutes your acceptance of the updated policy.
              </p>
            </Section>

            <Section title="10. Contact">
              <p>
                Questions or concerns about this privacy policy? Contact us at{' '}
                <a href="mailto:privacy@openipub.com" className="text-[#1a56a0] hover:underline">privacy@openipub.com</a>.
              </p>
              <p className="mt-3 text-gray-600">
                This privacy policy supersedes all previous versions, including the Open iPub Privacy Policy originally published in 2012.
              </p>
            </Section>

          </div>
        </main>
      </IonContent>
    </IonPage>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="mt-8 border-t border-gray-100 pt-6">
      <h2 className="mb-3 font-serif text-xl font-bold text-gray-900">{title}</h2>
      <div className="text-gray-600">{children}</div>
    </section>
  );
}

function ThirdParty({ name, url, purpose }: { name: string; url: string; purpose: string }) {
  return (
    <div className="rounded-xl border border-gray-100 bg-gray-50 px-4 py-3.5">
      <p className="font-semibold text-gray-800">
        {name}{' '}
        <a href={url} target="_blank" rel="noopener noreferrer" className="text-xs font-normal text-[#1a56a0] hover:underline">
          privacy policy ↗
        </a>
      </p>
      <p className="mt-1 text-sm text-gray-500">{purpose}</p>
    </div>
  );
}
